// How far a prospect's stations have got towards being published (CWA-154
// findings 5 + 6).
//
// A customer's wallbox reports itself available once they have finished
// installing and connecting it. Detection is an hourly sync, NOT a live feed —
// so nothing here may be worded as if it were current, and the age of the
// information is part of what an agent needs to see.
//
// Every field is optional: a backend that predates this release omits them, and
// an absent field must never be rendered as a value. "No stations" would be a
// lie about a prospect that has them.

import { StationReadiness } from "../types/onboarding";

interface ReadinessSource {
  station_readiness?: StationReadiness;
  available_stations?: number;
  total_stations?: number;
  ready_to_publish_since?: string | null;
  published_in_eround?: boolean;
}

// A resigned prospect has no readiness to show (CWA-228).
const READINESS_LABELS: Record<StationReadiness, string | null> = {
  NO_STATIONS: "No stations yet",
  AWAITING_CONFIGURATION: "Waiting for the customer",
  WAITING_FOR_SUPPORT: "Waiting for support",
  READY_TO_PUBLISH: "Ready to publish",
  LIVE: "Live",
  RESIGNED: null,
};

type Severity = "info" | "success" | "warning" | "danger" | null;

const READINESS_SEVERITY: Record<StationReadiness, Severity> = {
  NO_STATIONS: null,
  AWAITING_CONFIGURATION: "warning",
  // Ours to move, so it stands out from the customer's.
  WAITING_FOR_SUPPORT: "danger",
  READY_TO_PUBLISH: "success",
  LIVE: "info",
  RESIGNED: null,
};

/**
 * The readiness in plain language. Returns null for an absent value AND for one
 * this build does not recognise — a future backend value is omitted rather than
 * printed as an identifier (FR-030).
 */
export const readinessLabel = (
  status?: StationReadiness | null
): string | null => (status ? READINESS_LABELS[status] ?? null : null);

export const readinessSeverity = (
  status?: StationReadiness | null
): Severity => (status ? READINESS_SEVERITY[status] ?? null : null);

const usableCount = (value?: number): boolean =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

export interface AvailabilityProgress {
  available: number;
  total: number;
  label: string;
}

/**
 * Installation progress, for prospects where it says something.
 *
 * Null for a single-station prospect: "1 of 1" is noise on the common case, and
 * the readiness label already carries it. Null too for counts that cannot be
 * true — more available than the prospect has, or a total of zero — because
 * "3 of 0" reads as a bug rather than as progress.
 *
 * Null once the prospect is live, which is the case that prompted this: the
 * count is how many stations have an availability record, and the backend only
 * ever writes one when a wallbox reports itself in — so a customer whose second
 * box was never detected keeps "1 of 2" for good. Published stations are live
 * because an agent published the location, not because of that record, so after
 * go-live the two disagree on screen ("Live · 1 of 2 stations available") while
 * neither is wrong. It is a queue metric for deciding when to publish, and it
 * has no job once that decision is made.
 */
export const availabilityProgress = (
  source: ReadinessSource
): AvailabilityProgress | null => {
  const available = source?.available_stations;
  const total = source?.total_stations;
  if (!usableCount(available) || !usableCount(total)) return null;
  if ((total as number) < 2) return null;
  if ((available as number) > (total as number)) return null;
  if (source?.station_readiness === "LIVE") return null;
  return {
    available: available as number,
    total: total as number,
    label: `${available} of ${total} stations available`,
  };
};

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

/**
 * How long this prospect has been waiting on an agent, as text.
 *
 * The wording is deliberately about elapsed time rather than the current state
 * of anything: detection lags by up to an hour, so "ready for 2 hours" is
 * honest where "available now" would not be.
 */
export const readyDuration = (
  since?: string | null,
  now: Date = new Date()
): string | null => {
  if (!since) return null;
  const from = new Date(since).getTime();
  if (isNaN(from)) return null;
  const elapsed = now.getTime() - from;
  if (elapsed < 0) return null;

  if (elapsed < MS_PER_HOUR) return "ready for under an hour";
  if (elapsed < MS_PER_DAY) {
    const hours = Math.floor(elapsed / MS_PER_HOUR);
    return `ready for ${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  const days = Math.floor(elapsed / MS_PER_DAY);
  return `ready for ${days} ${days === 1 ? "day" : "days"}`;
};

/**
 * Whether to warn an agent that eRound still has this location private.
 *
 * Only when the flag says so. An absent flag is not a private location — the
 * warning asserts a fact about eRound, and without the flag there is no fact.
 */
export const needsPublishWarning = (source: ReadinessSource): boolean =>
  source?.published_in_eround === false;
